import {ComponentRef, Directive, ElementRef, HostListener, Input, TemplateRef, ViewContainerRef,} from '@angular/core';
import {TooltipComponent} from '../components/tooltip/tooltip.component';
import {PlatformService} from '../services/platform-service/platform.service';
import {TooltipPosition} from '../enums/tooltip-position.enum';

@Directive({
  standalone: true,
  selector: '[tooltip]',
})
export class TooltipDirective {
  @Input() tooltip: string = '';
  @Input() tooltipTemplateRef: TemplateRef<unknown> | undefined;
  @Input() toolTipMsBeforeShowing: number = 600;
  @Input() toolTipElementRef?: HTMLElement;
  @Input() toolTipWidthDefault: boolean = false;
  @Input() tooltipPosition: TooltipPosition = TooltipPosition.Top;
  @Input() tooltipMarginTop: number = 0;

  private isShowTooltip = false;
  private tooltipComponentRef: ComponentRef<TooltipComponent> | undefined;

  constructor(
    private readonly elementRef: ElementRef,
    private readonly viewContainerRef: ViewContainerRef,
    private platformService: PlatformService,
  ) {
  }

  @HostListener('mouseenter')
  public onMouseEnter(): void {
    this.isShowTooltip = true;
    setTimeout(() => {
      const shouldShowTooltip = this.isShowTooltip;
      if (!this.platformService.isMobile() && shouldShowTooltip) {
        this.showTooltip();
      }
    }, this.toolTipMsBeforeShowing);
  }

  @HostListener('mouseleave')
  public onMouseLeave(): void {
    this.isShowTooltip = false;
    this.destroy();
  }

  private showTooltip(): void {
    if (!this.tooltipComponentRef) {
      this.tooltipComponentRef =
        this.viewContainerRef.createComponent(TooltipComponent);
      this.setTooltipComponentProperties();
    }
  }

  private setTooltipComponentProperties() {
    if (!this.tooltipComponentRef) return;

    const tooltipHost = this.getTooltipHostElement();
    const tooltipRect = tooltipHost.getBoundingClientRect();
    const tooltipComponent = this.tooltipComponentRef.instance;

    // Update width if no default width is provided
    if (!this.toolTipWidthDefault) {
      tooltipComponent.width = this.getTooltipContentWidth(tooltipHost);
    }

    tooltipComponent.tooltip = this.tooltip;
    tooltipComponent.contentRef = this.tooltipTemplateRef;
    this.tooltipComponentRef.location.nativeElement.style.zIndex = '10';

    // Calculate position based on the host element
    this.setTooltipPosition(tooltipComponent, tooltipRect);
  }

  private getTooltipHostElement(): HTMLElement {
    return this.toolTipElementRef || this.elementRef.nativeElement;
  }

  private getTooltipContentWidth(element: HTMLElement): number {
    return element.getBoundingClientRect().width;
  }

  private setTooltipPosition(instance: TooltipComponent, rect: DOMRect): void {
    const {top, left, right} = rect;

    switch (this.tooltipPosition) {
      case TooltipPosition.Top:
        instance.bottom = window.innerHeight - top + this.tooltipMarginTop;
        instance.left = (left + right) / 2;
        break;

      case TooltipPosition.Bottom:
        instance.left = (left + right) / 2;
        instance.top = top + (this.tooltip.length < 20 ? 20 : 33);
        break;

      default:
        break;
    }
  }

  private destroy(): void {
    if (this.tooltipComponentRef) {
      this.tooltipComponentRef.destroy();
      this.tooltipComponentRef = undefined;
    }
  }
}
